import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemIcon,
    Checkbox,
    ListItemText,
    Typography,
    IconButton,
    Box
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';

interface ManualEntryModalProps {
    data: any[];
    dialogOpen: boolean;
    setDialogOpen: (open: boolean) => void;
}

const CertEntryModal: React.FC<ManualEntryModalProps> = ({ data = [], dialogOpen, setDialogOpen }) => {
    const [selectedDays, setSelectedDays] = useState<Record<number, boolean>>({});
    const [selectedReceipts, setSelectedReceipts] = useState<Record<string, boolean>>({});
    const [selectAll, setSelectAll] = useState(false);

    const handleDialogClose = () => setDialogOpen(false);

    const formatDate = (date: Date) => date.toLocaleDateString('en-GB').replace(/\//g, '-');

    // ** Toggle individual receipt selection **
    const toggleReceiptSelection = (receipt: any, timestamp: number) => {
        const receiptKey = `${receipt.id}-${timestamp}`;
        const dayKey = timestamp;

        setSelectedReceipts((prevReceipts) => {
            const updatedReceipts = { ...prevReceipts, [receiptKey]: !prevReceipts[receiptKey] };

            // Check if all receipts of this day are selected
            const allReceipts = data.find((day) => day.date.getTime() === timestamp)?.receipts || [];
            const allSelected = allReceipts.every((r) => updatedReceipts[`${r.id}-${timestamp}`]);

            // If at least one receipt is selected, mark the day as selected
            setSelectedDays((prevDays) => ({ ...prevDays, [dayKey]: allSelected }));

            return updatedReceipts;
        });
    };

    // ** Toggle day selection (selects/deselects all receipts) **
    const toggleDaySelection = (timestamp: number, receipts: any[]) => {
        console.log('timestamp', timestamp);
        const isSelected = !selectedDays[timestamp];
        setSelectedDays((prev) => ({ ...prev, [timestamp]: isSelected }));

        setSelectedReceipts((prevReceipts) => {
            const updatedReceipts = { ...prevReceipts };
            receipts.forEach((receipt) => {
                updatedReceipts[`${receipt.id}-${timestamp}`] = isSelected;
            });
            return updatedReceipts;
        });
    };

    // ** Toggle Select All **
    const toggleSelectAll = () => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);

        const updatedDays: Record<number, boolean> = {};
        const updatedReceipts: Record<string, boolean> = {};

        if (newSelectAll) {
            data.forEach((day) => {
                const timestamp = day.date.getTime();
                updatedDays[timestamp] = true; // Fix: Ensure all days are selected
                day.receipts.forEach((receipt) => {
                    updatedReceipts[`${receipt.id}-${timestamp}`] = true;
                });
            });
        }

        setSelectedDays(updatedDays);
        setSelectedReceipts(updatedReceipts);
    };

    const handleConfirmSelection = () => {
        const selectedData = data.flatMap((day) =>
            day.receipts.filter((receipt) => selectedReceipts[`${receipt.id}-${day.date.getTime()}`])
        );
        console.log('Selected receipts:', selectedData);
        setDialogOpen(false);
    };

    return (
        <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth>
            <DialogTitle sx={{ alignSelf: 'center' }}>בחר תעודות פתוחות</DialogTitle>
            <DialogContent>
                {/* Select All Checkbox */}
                <ListItem sx={{ justifyContent: 'center' }}>
                    <ListItemIcon>
                        <Checkbox checked={selectAll} onChange={toggleSelectAll} />
                    </ListItemIcon>
                    <Typography variant="h6">בחר הכל</Typography>
                </ListItem>

                <List>
                    {data.length > 0 ? (
                        data.map((day) => {
                            const timestamp = day.date.getTime();
                            const allReceiptsSelected = day.receipts.every((receipt) => selectedReceipts[`${receipt.id}-${timestamp}`]);
                            const someReceiptsSelected = day.receipts.some((receipt) => selectedReceipts[`${receipt.id}-${timestamp}`]);

                            const totalPrice = day.receipts.reduce((sum, receipt) => sum + (Number(receipt.price) || 0), 0);

                            return (
                                <div key={timestamp}>
                                    {/* Day Checkbox */}
                                    <ListItem
                                        sx={{
                                            paddingX: '15px',
                                            borderRadius: '5px',
                                            marginBottom: '5px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <ListItemIcon>
                                                <Checkbox
                                                    checked={!!selectedDays[timestamp]}
                                                    indeterminate={someReceiptsSelected && !allReceiptsSelected}
                                                    onChange={() => toggleDaySelection(timestamp, day.receipts)}
                                                    sx={{ color: 'red' }}
                                                />
                                            </ListItemIcon>
                                            <Typography variant="h6" sx={{ color: 'red', fontSize: '14px', fontWeight: 'bold' }}>
                                                {formatDate(day.date)}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'red' }}>
                                            {Number(totalPrice).toFixed(2)}
                                        </Typography>
                                    </ListItem>

                                    {day.receipts.map((receipt) => (
                                        <ListItem key={`${receipt.id}-${timestamp}`} sx={{ paddingLeft: '30px' }}>
                                            <ListItemIcon>
                                                <Checkbox
                                                    checked={!!selectedReceipts[`${receipt.id}-${timestamp}`]}
                                                    onChange={() => toggleReceiptSelection(receipt, timestamp)}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={`${receipt.id} | ${formatDate(receipt.date)} | ${Number(
                                                    receipt.price || 0
                                                ).toFixed(2)}`}
                                            />
                                            <IconButton onClick={() => console.log('Receipt clicked:', receipt)}>
                                                <VisibilityIcon />
                                            </IconButton>
                                        </ListItem>
                                    ))}
                                </div>
                            );
                        })
                    ) : (
                        <ListItem>
                            <ListItemText primary="אין נתונים להצגה" />
                        </ListItem>
                    )}
                </List>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
                <Button onClick={handleConfirmSelection} color="primary" variant="contained">
                    בחר
                </Button>
                <Button onClick={handleDialogClose} color="secondary" variant="contained">
                    בטל
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CertEntryModal;
